/**
 * Component is used to show a shipping-countdown in minutes.
 *
 *  @attr {String} [end-time] - Date to count down to
 *  @attr {String} [expire-label] - Short description for remaining time
 *  @attr {String} [time-slot-label] - Long description which display the time slot selected for the user
 *  @attr {Number} [minutes] - Minutes remained from countdown
 *  @attr {Number} [seconds] - Seconds remained from countdown
 *  @attr {String} [showShippingCountdown] - Redux value for showing the countdown
 *
 *  @param {boolean} invalidDateTime - become true when the invalid date is provided (that can't be parced with new Date())
 *
 */

import { html } from 'lit-element';
import moment from 'moment';

import { SimpleLitElement } from '@elab/components';
import connect from '@elab/core/store/connect';
import { selectors } from '@elab/core/store/checkout';
import { selectors as cartSelectors } from '@elab/core/store/cart';
import {
    deadlineDateUtil,
    getTimeRemaining,
    initializeCountdownTimer,
} from '@elab/core/utils/countdownUtils';

const edgeUnit = 'MINUTES';
const CLASS_NAME = 'shipping-countdown';
const SHIP_FROM_STORE = 'SHIP-FROM-STORE';
export class ShippingCountdown extends SimpleLitElement {
    static get properties() {
        return {
            endTime: { type: String, attribute: 'end-time' },
            expireLabel: { type: String, attribute: 'expire-label' },
            timeSlotLabel: { type: String, attribute: 'time-slot-label' },
            invalidDateTime: { type: Boolean, reflect: true },
            minutes: { type: Number },
            seconds: { type: Number },
            showShippingCountdown: { type: Boolean },
            deliveryMethodCode: { type: String, attribute: false },
        };
    }

    constructor() {
        super();
        this.endTime = moment();
        this.expireLabel = '';
        this.timeSlotLabel = '';
        this.invalidDateTime = false;
        this.minutes = 0;
        this.seconds = 0;
        this.deliveryMethodCode = '';
    }

    firstUpdated() {
        this.setTimeInterval(this.updateClock, 1000);
    }

    setTimeInterval(callback, time) {
        clearInterval(this.timeinterval);
        this.timeinterval = initializeCountdownTimer(callback, time);
    }

    updated(_changedProperties) {
        if (_changedProperties.has('endTime')) {
            this.setTimeInterval(this.updateClock, 1000);
        }
    }

    disconnectedCallback() {
        clearInterval(this.timeinterval);
    }

    updateClock = () => {
        const { minutes, seconds } = getTimeRemaining(this.deadlineDate, edgeUnit);

        this.minutes = minutes;
        this.seconds = seconds;

        if (minutes < 1 && seconds <= 0 && this.deliveryMethodCode === SHIP_FROM_STORE) {
            clearInterval(this.timeinterval);
            this.dispatchEvent(
                new CustomEvent('countdown-end', {
                    detail: {
                        type: 'checkout.SHIPPING_COUNTDOWN_ENDED',
                    },
                }),
            );
        }
    };

    get deadlineDate() {
        let endDate = moment(this.endTime);
        let isPast = endDate < moment();

        if (endDate && endDate.isValid() && !isPast) {
            this.invalidDateTime = false;
        } else {
            this.invalidDateTime = true;
            endDate = moment();
        }

        return deadlineDateUtil(endDate);
    }

    formatValue(minutes, seconds) {
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }

    getElementTextTemplate(label) {
        return html`
            <i class="${CLASS_NAME}__icon"></i>
            <span class="${CLASS_NAME}__label">
                ${label}
            </span>
            ${this.countdownTemplate}
        `;
    }

    getTimeSlotTemplate(label) {
        return html`
            <div class="${CLASS_NAME}__wrapper">
                <i class="${CLASS_NAME}__icon"></i>
                ${this.countdownTemplate}
            </div>
            <span class="${CLASS_NAME}__label">${label}</span>
        `;
    }

    get countdownTemplate() {
        return html`
            <span class="${CLASS_NAME}__timer">
                ${this.formatValue(this.minutes, this.seconds)}
            </span>
        `;
    }

    render() {
        return this.showShippingCountdown && !this.invalidDateTime
            ? html`
                  <div class="${CLASS_NAME} ${this.timeSlotLabel ? `${CLASS_NAME}--full` : ''}">
                      ${this.timeSlotLabel
                          ? this.getTimeSlotTemplate(this.timeSlotLabel)
                          : this.getElementTextTemplate(this.expireLabel)}
                  </div>
              `
            : html``;
    }
}

const mapStateToProps = state => ({
    showShippingCountdown: selectors.showShippingCountdown(state),
    deliveryMethodCode: cartSelectors.getDeliveryModeCode(state),
});

const mapDispatchToEvents = dispatch => ({
    'countdown-end': ({ detail }) => {
        dispatch({ type: detail.type });
    },
});

export default () =>
    customElements.define(
        'e2-shipping-countdown',
        connect(mapStateToProps, mapDispatchToEvents)(ShippingCountdown),
    );
